import { GHALISHOR_API_V1_PREFIX } from './identity-access';
import type { IsoDateTimeString, UuidString } from './identity-access';

export const SERVICE_CATALOG = Object.freeze([
  'washing',
  'washing_standard',
  'washing_premium',
  'washing_delicate',
  'washing_antique',
  'washing_silk',
  'washing_kilim',
  'washing_gabbeh',
  'washing_tableau',
  'dusting',
  'dry_clean_carpet',
  'stain_removal',
  'color_bleed_fix',
  'dyeing',
  'color_fix',
  'repair',
  'repair_edge',
  'repair_hole',
  'repair_burn',
  'moth_treatment',
  'fringe_restore',
  'shirazeh',
  'metghal',
  'leather_edge',
  'serging',
  'protection',
  'protection_nano',
  'shearing_pay',
  'pressing',
  'scent_finish',
  'cover_pack',
  'disinfect',
  'washing_moket',
  'washing_sofa',
  'washing_curtain',
  'washing_mattress',
  'washing_blanket',
  'dry_clean_on_site',
] as const);

export type ServiceCatalogKey = (typeof SERVICE_CATALOG)[number];

/** Item / goods type (stored in carpet_type column for compatibility). */
export const CARPET_TYPES = Object.freeze([
  'handmade',
  'machine_made',
  'silk',
  'kilim',
  'gabbeh',
  'moket',
  'tableau',
  'sofa',
  'curtain',
  'mattress',
  'blanket',
  'other',
] as const);
export type CarpetType = (typeof CARPET_TYPES)[number];
export type ItemType = CarpetType;

/** One independently configured item inside a combined customer request. */
export interface ServiceRequestUnit {
  /** Number of pieces that share this size. */
  readonly quantity: number;
  /** Optional spoken/measured length in metres. */
  readonly lengthM?: number | null;
  /** Optional spoken/measured width in metres. */
  readonly widthM?: number | null;
  /** Area of each piece, not the group total. */
  readonly areaSqm: number;
}

export interface ServiceRequestItem {
  readonly itemType: CarpetType;
  readonly count: number;
  /** Total approximate area for all units in this item. */
  readonly approximateSqm: number;
  /**
   * How the area was obtained. `onsite` keeps the request moving when the
   * customer does not know the size; pricing must wait for pickup measurement.
   */
  readonly measurementStatus?: 'provided' | 'estimated' | 'onsite';
  readonly services: readonly ServiceCatalogKey[];
  /** Per-size breakdown captured by the conversational assistant. */
  readonly units?: readonly ServiceRequestUnit[];
}

export const SERVICE_REQUEST_STATUSES = Object.freeze([
  'draft',
  'submitted',
  'matching',
  'offered',
  'waiting_acceptance',
  'rejected_by_business',
  'accepted',
  'contact_pending',
  'price_pending',
  'schedule_pending',
  'scheduled',
  'completed',
  'cancelled',
] as const);
export type ServiceRequestStatus = (typeof SERVICE_REQUEST_STATUSES)[number];

/** Pre-assignment: not yet accepted by a business. */
export const LIVE_REQUEST_STATUSES = Object.freeze([
  'draft',
  'submitted',
  'matching',
  'offered',
  'waiting_acceptance',
  'rejected_by_business',
] as const);

/** Accepted / in progress with a business. */
export const ACCEPTED_REQUEST_STATUSES = Object.freeze([
  'accepted',
  'contact_pending',
  'price_pending',
  'schedule_pending',
  'scheduled',
] as const);

/** Customer may cancel until completed/cancelled. */
export const CANCELLABLE_REQUEST_STATUSES = Object.freeze([
  ...LIVE_REQUEST_STATUSES,
  ...ACCEPTED_REQUEST_STATUSES,
] as const);

export const MATCHING_FLOW_TYPES = Object.freeze(['curated_offers', 'auto_broadcast'] as const);
export type MatchingFlowType = (typeof MATCHING_FLOW_TYPES)[number];

export const WAVE2_BUNDLES = Object.freeze(['faster', 'economical', 'higher_guarantee'] as const);
export type Wave2Bundle = (typeof WAVE2_BUNDLES)[number];

export const CUSTOMER_MATCHING_PREFERENCES = Object.freeze([
  'balanced', 'economical', 'fastest', 'quality',
] as const);
export type CustomerMatchingPreference = (typeof CUSTOMER_MATCHING_PREFERENCES)[number];

export const OFFER_STATUSES = Object.freeze([
  'pending',
  'accepted',
  'rejected',
  'expired',
  'withdrawn',
] as const);
export type OfferStatus = (typeof OFFER_STATUSES)[number];

export const DEFAULT_MATCHING_BROADCAST_LIMIT = 5;

export const PROFILE_ROUTES = Object.freeze({
  profile: `${GHALISHOR_API_V1_PREFIX}/profile`,
  reverseGeocode: (lat: number, lng: number) =>
    `${GHALISHOR_API_V1_PREFIX}/profile/reverse-geocode?lat=${encodeURIComponent(String(lat))}&lng=${encodeURIComponent(String(lng))}`,
} as const);

export const CUSTOMER_REQUEST_ROUTES = Object.freeze({
  list: `${GHALISHOR_API_V1_PREFIX}/customer/requests`,
  create: `${GHALISHOR_API_V1_PREFIX}/customer/requests`,
  byId: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}`,
  submit: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}/submit`,
  cancel: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}/cancel`,
  reorder: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}/reorder`,
  offers: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}/offers`,
  selectOffer: (id: string, offerId: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}/offers/${encodeURIComponent(offerId)}/select`,
  wave2: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}/wave2`,
} as const);

export const BUSINESS_REQUEST_ROUTES = Object.freeze({
  inbox: `${GHALISHOR_API_V1_PREFIX}/business/requests/inbox`,
  acceptOffer: (offerId: string) =>
    `${GHALISHOR_API_V1_PREFIX}/business/requests/offers/${encodeURIComponent(offerId)}/accept`,
  rejectOffer: (offerId: string) =>
    `${GHALISHOR_API_V1_PREFIX}/business/requests/offers/${encodeURIComponent(offerId)}/reject`,
} as const);

export interface ServiceRequestSummary {
  readonly id: UuidString;
  readonly customerUserId: UuidString;
  readonly status: ServiceRequestStatus;
  readonly carpetType: CarpetType;
  readonly carpetCount: number;
  readonly approximateSqm: number;
  readonly services: readonly ServiceCatalogKey[];
  /** Full combined basket. Legacy top-level fields mirror the first item. */
  readonly items: readonly ServiceRequestItem[];
  readonly notes: string | null;
  readonly flowType: MatchingFlowType | null;
  readonly matchingWave: number;
  readonly acceptedBusinessId: UuidString | null;
  readonly wave2Bundle: Wave2Bundle | null;
  readonly scheduledPickupAt: IsoDateTimeString | null;
  readonly scheduledReturnAt: IsoDateTimeString | null;
  readonly scheduleNotes: string | null;
  readonly scheduleState: 'none' | 'proposed' | 'counter_proposed' | 'confirmed' | null;
  readonly scheduleProposedByRole: 'business' | 'customer' | null;
  readonly scheduleConfirmDeadlineAt: IsoDateTimeString | null;
  readonly scheduleConfirmedAt: IsoDateTimeString | null;
  readonly confirmedPrice: number | null;
  /** Customer max acceptable return/turnaround days (1–14). */
  readonly maxReturnWaitDays: number | null;
  readonly matchingPreference: CustomerMatchingPreference;
  readonly needPickup: boolean;
  readonly needDelivery: boolean;
  readonly floorCount: number;
  readonly hasElevator: boolean;
  readonly submittedAt: IsoDateTimeString | null;
  readonly createdAt: IsoDateTimeString;
  readonly updatedAt: IsoDateTimeString;
}

export interface CreateServiceRequestRequest {
  readonly items?: readonly ServiceRequestItem[];
  readonly carpetType?: CarpetType;
  readonly carpetCount?: number;
  readonly approximateSqm?: number;
  readonly services?: readonly ServiceCatalogKey[];
  readonly notes?: string | null;
  /** Max days customer will wait for return (1–14). Used in matching. */
  readonly maxReturnWaitDays?: number | null;
  readonly matchingPreference?: CustomerMatchingPreference;
  readonly needPickup?: boolean;
  readonly needDelivery?: boolean;
  readonly floorCount?: number;
  readonly hasElevator?: boolean;
}

export interface UpdateServiceRequestRequest {
  readonly items?: readonly ServiceRequestItem[];
  readonly carpetType?: CarpetType;
  readonly carpetCount?: number;
  readonly approximateSqm?: number;
  readonly services?: readonly ServiceCatalogKey[];
  readonly notes?: string | null;
  readonly maxReturnWaitDays?: number | null;
  readonly matchingPreference?: CustomerMatchingPreference;
  readonly needPickup?: boolean;
  readonly needDelivery?: boolean;
  readonly floorCount?: number;
  readonly hasElevator?: boolean;
}

export interface SubmitServiceRequestRequest {
  readonly flowType?: MatchingFlowType;
}

export interface SubmitServiceRequestResponse {
  readonly request: ServiceRequestSummary;
  readonly message: string;
}

export interface OfferSummary {
  readonly id: UuidString;
  readonly serviceRequestId: UuidString;
  readonly businessId: UuidString;
  readonly businessName: string;
  readonly status: OfferStatus;
  readonly wave: number;
  readonly offeredAt: IsoDateTimeString;
  readonly respondedAt: IsoDateTimeString | null;
  readonly distanceKm: number | null;
  readonly estimatedPrice: number | null;
  readonly ratingAvg: number | null;
  readonly ratingCount: number;
  readonly matchScore: number | null;
  readonly badges: readonly string[];
  readonly recommendationReasons: readonly string[];
  readonly priceLines: readonly {
    readonly serviceKey: string;
    readonly unit: string;
    readonly unitPrice: number;
    readonly amount: number;
  }[];
  readonly logisticsCosts: {
    readonly pickup: number;
    readonly delivery: number;
    readonly floorCarry: number;
    readonly total: number;
  };
  readonly typicalReturnDays: number | null;
  readonly autoQuote: boolean;
  readonly autoSchedule: boolean;
  readonly proposedPickupAt: IsoDateTimeString | null;
  readonly proposedReturnAt: IsoDateTimeString | null;
}

export interface BusinessInboxItem {
  readonly offerId: UuidString;
  readonly serviceRequestId: UuidString;
  readonly status: OfferStatus;
  readonly wave: number;
  readonly offeredAt: IsoDateTimeString;
  readonly request: {
    readonly carpetType: CarpetType;
    readonly carpetCount: number;
    readonly approximateSqm: number;
    readonly services: readonly ServiceCatalogKey[];
    readonly items: readonly ServiceRequestItem[];
    readonly notes: string | null;
    readonly flowType: MatchingFlowType | null;
    readonly matchingWave: number;
    readonly status: ServiceRequestStatus;
    readonly maxReturnWaitDays: number | null;
  };
}

export interface ApplyWave2Request {
  readonly bundle: Wave2Bundle;
}

/** Cancel permanently removes the request (no cancelled history). */
export interface CancelServiceRequestResponse {
  readonly id: UuidString;
  readonly deleted: true;
}

export interface ReorderServiceRequestRequest {
  /** Prefer previous business; omit/null = open matching for a new business. */
  readonly preferredBusinessId?: UuidString | null;
  readonly carpetType?: CarpetType;
  readonly carpetCount?: number;
  readonly approximateSqm?: number;
  readonly services?: readonly ServiceCatalogKey[];
  readonly notes?: string | null;
  readonly maxReturnWaitDays?: number | null;
  readonly flowType?: MatchingFlowType;
}

export interface ReorderPreferredBusinessInfo {
  readonly id: UuidString;
  readonly name: string;
  /** active+recent = online; active+stale = offline_followup; otherwise inactive */
  readonly availability: 'online' | 'offline_followup' | 'inactive';
}

export interface ReorderServiceRequestResponse {
  readonly request: ServiceRequestSummary;
  readonly message: string;
  readonly preferredBusiness: ReorderPreferredBusinessInfo | null;
  readonly alternatives: readonly OfferSummary[];
}

export function isServiceCatalogKey(value: unknown): value is ServiceCatalogKey {
  return typeof value === 'string' && (SERVICE_CATALOG as readonly string[]).includes(value);
}

export function isCarpetType(value: unknown): value is CarpetType {
  return typeof value === 'string' && (CARPET_TYPES as readonly string[]).includes(value);
}

export function isServiceRequestStatus(value: unknown): value is ServiceRequestStatus {
  return typeof value === 'string' && (SERVICE_REQUEST_STATUSES as readonly string[]).includes(value);
}

export function isMatchingFlowType(value: unknown): value is MatchingFlowType {
  return typeof value === 'string' && (MATCHING_FLOW_TYPES as readonly string[]).includes(value);
}

export function isWave2Bundle(value: unknown): value is Wave2Bundle {
  return typeof value === 'string' && (WAVE2_BUNDLES as readonly string[]).includes(value);
}

export function isCustomerMatchingPreference(value: unknown): value is CustomerMatchingPreference {
  return typeof value === 'string' && (CUSTOMER_MATCHING_PREFERENCES as readonly string[]).includes(value);
}

export function isOfferStatus(value: unknown): value is OfferStatus {
  return typeof value === 'string' && (OFFER_STATUSES as readonly string[]).includes(value);
}
